import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Lock, ArrowRight, Loader2 } from "lucide-react";
import { GUIDES } from "@/constants/testIds";
import { ConsentCheckbox } from "./ConsentCheckbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GuideCheckoutModal = ({ guide, onClose }) => {
  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !/^\S+@\S+\.\S+$/.test(email)) {
      toast.error("Please add your first name and a valid email.");
      return;
    }
    if (!consent) {
      toast.error("Please tick the acknowledgement to continue.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/guides/checkout`, {
        guide_id: guide.id,
        email: email.trim(),
        first_name: firstName.trim(),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      setLoading(false);
      toast.error("Couldn't start checkout — please try again.");
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-[var(--ink)]/60 backdrop-blur-sm"
      onClick={onClose}
      data-testid={GUIDES.modal}
    >
      <div
        className="w-full max-w-md bg-[var(--paper)] rounded-3xl border border-[var(--line)] p-7 md:p-8 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <span className="num-tag text-xs">{guide.free ? "Free PDF guide" : "PDF guide"}</span>
            <h3 className="font-display text-2xl font-extrabold mt-2 leading-tight">{guide.title}</h3>
          </div>
          <button
            type="button"
            data-testid={GUIDES.close}
            onClick={onClose}
            className="shrink-0 p-2 rounded-full hover:bg-[var(--mist-soft)] transition-colors"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[var(--ocean)] mb-1.5">
              First name
            </label>
            <input
              data-testid={GUIDES.firstName}
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Your first name"
              className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base focus:outline-none focus:border-[var(--ink)] transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest font-bold text-[var(--ocean)] mb-1.5">
              Email — we'll send your download link here
            </label>
            <input
              data-testid={GUIDES.email}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-base focus:outline-none focus:border-[var(--ink)] transition-colors"
            />
          </div>

          <ConsentCheckbox checked={consent} onChange={setConsent} testId="guide-consent" />

          <button
            type="submit"
            data-testid={GUIDES.submit}
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-[var(--ink)] text-[var(--paper)] font-display font-bold rounded-xl px-6 py-3.5 hover:bg-[var(--ink-soft)] transition-colors disabled:opacity-60"
          >
            {loading ? (
              <Loader2 size={18} className="animate-spin" />
            ) : guide.free ? (
              <>
                Get the free guide <ArrowRight size={16} />
              </>
            ) : (
              <>
                Continue to secure checkout <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-[var(--ocean)]">
          <Lock size={12} />{" "}
          {guide.free
            ? "No payment needed · Instant PDF download"
            : "Secure checkout by Stripe · Instant PDF download after payment"}
        </p>
      </div>
    </div>
  );
};
